import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  createColumnHelper,
  type SortingState,
} from '@tanstack/react-table';
import type { Override, PaginationInfo } from '../api/types';
import { SHELVE_LABELS } from '../api/types';

const col = createColumnHelper<Override>();

const columns = [
  col.accessor('id', { header: 'ID', size: 60 }),
  col.accessor('carrierCode', { header: 'Carrier', size: 70 }),
  col.accessor('bookingCode', {
    header: 'Booking',
    size: 100,
    cell: (info) => info.getValue() || '-',
  }),
  col.accessor('fareFamily', { header: 'Fare Family', size: 140 }),
  col.accessor('correctShelve', {
    header: 'Correct Shelf',
    size: 120,
    cell: (info) => {
      const v = info.getValue();
      return `${v} (${SHELVE_LABELS[v] || v})`;
    },
  }),
  col.accessor('wrongShelve', {
    header: 'Wrong Shelf',
    size: 120,
    cell: (info) => {
      const v = info.getValue();
      return v ? `${v} (${SHELVE_LABELS[v] || v})` : '-';
    },
  }),
  col.accessor('confirmed', { header: 'Confirmed', size: 80 }),
  col.accessor('gds', {
    header: 'GDS',
    size: 60,
    cell: (info) => {
      const v = info.getValue();
      return v === 's' ? 'Sabre' : v === 'a' ? 'Amadeus' : '-';
    },
  }),
  col.accessor('comments', {
    header: 'Comments',
    size: 200,
    cell: (info) => {
      const v = info.getValue();
      if (!v) return '-';
      return v.length > 50 ? v.slice(0, 50) + '...' : v;
    },
  }),
];

function confirmedRowColor(confirmed: string): string {
  switch (confirmed) {
    case 'Yes':
      return 'bg-green-50';
    case 'QA Only':
      return 'bg-orange-50';
    default:
      return 'bg-gray-50';
  }
}

interface Props {
  data: Override[];
  pagination: PaginationInfo;
  sorting: SortingState;
  onSortingChange: (sorting: SortingState) => void;
  onPageChange: (page: number) => void;
  onEdit: (override: Override) => void;
  onDelete: (override: Override) => void;
  loading: boolean;
}

export function OverrideTable({
  data,
  pagination,
  sorting,
  onSortingChange,
  onPageChange,
  onEdit,
  onDelete,
  loading,
}: Props) {
  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: (updater) => {
      const next = typeof updater === 'function' ? updater(sorting) : updater;
      onSortingChange(next);
    },
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    manualPagination: true,
    pageCount: pagination.total_pages,
  });

  return (
    <div>
      <div className="overflow-x-auto border border-gray-200 rounded-lg">
        <table className="w-full text-sm">
          <thead className="bg-gray-100">
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id}>
                {hg.headers.map((header) => (
                  <th
                    key={header.id}
                    className="px-3 py-2 text-left font-medium text-gray-700 cursor-pointer select-none whitespace-nowrap"
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                    {header.column.getIsSorted() === 'asc'
                      ? ' ▲'
                      : header.column.getIsSorted() === 'desc'
                        ? ' ▼'
                        : ''}
                  </th>
                ))}
                <th className="px-3 py-2 text-left font-medium text-gray-700 w-20">
                  Actions
                </th>
              </tr>
            ))}
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={columns.length + 1} className="text-center py-8 text-gray-400">
                  Loading...
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={columns.length + 1} className="text-center py-8 text-gray-400">
                  No results found
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  className={`border-t border-gray-100 hover:bg-blue-50 ${confirmedRowColor(row.original.confirmed)}`}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="px-3 py-1.5 whitespace-nowrap">
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                  <td className="px-3 py-1.5 whitespace-nowrap">
                    <button
                      onClick={() => onEdit(row.original)}
                      className="text-blue-600 hover:text-blue-800 text-xs mr-2"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => onDelete(row.original)}
                      className="text-red-600 hover:text-red-800 text-xs"
                    >
                      Del
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between mt-3 text-sm text-gray-600">
        <span>
          Showing {(pagination.page - 1) * pagination.per_page + 1}-
          {Math.min(pagination.page * pagination.per_page, pagination.total)} of{' '}
          {pagination.total} rules
        </span>
        <div className="flex gap-1">
          <button
            disabled={pagination.page <= 1}
            onClick={() => onPageChange(pagination.page - 1)}
            className="px-2 py-1 border rounded disabled:opacity-30"
          >
            Prev
          </button>
          {Array.from({ length: Math.min(pagination.total_pages, 7) }, (_, i) => {
            let page: number;
            if (pagination.total_pages <= 7) {
              page = i + 1;
            } else if (pagination.page <= 4) {
              page = i + 1;
            } else if (pagination.page >= pagination.total_pages - 3) {
              page = pagination.total_pages - 6 + i;
            } else {
              page = pagination.page - 3 + i;
            }
            return (
              <button
                key={page}
                onClick={() => onPageChange(page)}
                className={`px-2 py-1 border rounded ${
                  page === pagination.page
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'hover:bg-gray-100'
                }`}
              >
                {page}
              </button>
            );
          })}
          <button
            disabled={pagination.page >= pagination.total_pages}
            onClick={() => onPageChange(pagination.page + 1)}
            className="px-2 py-1 border rounded disabled:opacity-30"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
